import type { ReactElement, ReactNode } from "react";
import { Fragment } from "react";
import type { FooterMenuProps } from "./FooterMenu";
import styled from "styled-components";

import { colors } from "../../../styles/figmaColors";
import { typographies } from "../../../styles/figmaTypographies";
import { StyledColumn, StyledFlex } from "./layout";
import { Spacer } from "../Spacer";
import { Brand } from "./Brand";
import { BackButton } from "./buttons/BackButton";
import { Button } from "./buttons/Button";
import { TextCapitalized } from "../text/TextCapitalized";
import { ToastContainerDS as ToastContainer } from "../Toast";

export type PageTone = "light" | "dark";

export interface PageTopAction {
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
}

export interface PageProps {
  tone?: PageTone;
  onBack?: () => void;
  title?: string;
  subtitle?: ReactNode;
  topActions?: PageTopAction[];
  footerMenu?: ReactElement<FooterMenuProps>;
  children?: ReactNode;
}

// Figma mockup value - the palette has no pure-black token.
const CHARGING_BLACK = "#0E1112";

// Figma "Modal shadow" — no shadow token in the design system.
const MODAL_SHADOW =
  "0 0.125rem 0.3125rem rgba(143, 143, 143, 0.15), 0 0 0.9375rem rgba(143, 143, 143, 0.15)";

const TONE_STYLES: Record<PageTone, { background: string; text: string }> = {
  light: {
    background: colors["colors/surfaces/background/background_level0"],
    text: colors["colors/text/black"],
  },
  dark: {
    background: CHARGING_BLACK,
    text: colors["colors/text/white"],
  },
};

const PageTopActions = ({
  tone,
  topActions,
}: {
  tone: PageTone;
  topActions: PageTopAction[];
}) => (
  <>
    {topActions.map((action, index) => (
      <Fragment key={action.label}>
        {index > 0 ? <Spacer x={0.75} /> : null}
        <Button tone={tone} icon={action.icon} onClick={action.onClick}>
          <TextCapitalized>{action.label}</TextCapitalized>
        </Button>
      </Fragment>
    ))}
  </>
);

const PageHeader = ({
  tone,
  onBack,
  title,
  subtitle,
  topActions,
}: Pick<PageProps, "onBack" | "title" | "subtitle" | "topActions"> & {
  tone: PageTone;
}) => (
  <StyledHeader $tone={tone}>
    <Spacer y={1} />
    <StyledTopRow>
      <Spacer x={1.5} />
      <Brand />
      <Spacer x={1.5} />
    </StyledTopRow>
    {topActions ? (
      <>
        <Spacer y={1} />
        <StyledTopRow>
          <Spacer x={1.5} />
          <StyledTopActions>
            <PageTopActions tone={tone} topActions={topActions} />
          </StyledTopActions>
          <Spacer x={1.5} />
        </StyledTopRow>
      </>
    ) : null}
    {onBack ? (
      <StyledTopBar>
        <Spacer y={1} />
        <StyledTopRow>
          <Spacer x={1.5} />
          <BackButton onClick={onBack} />
        </StyledTopRow>
        <Spacer y={0.5} />
      </StyledTopBar>
    ) : null}
    {title ? (
      <>
        <Spacer y={2} />
        <StyledTopRow>
          <Spacer x={1.5} />
          <StyledTitleColumn>
            <StyledTitle>
              <TextCapitalized>{title}</TextCapitalized>
            </StyledTitle>
            {subtitle ? (
              <>
                <Spacer y={0.5} />
                <StyledSubtitle>{subtitle}</StyledSubtitle>
              </>
            ) : null}
          </StyledTitleColumn>
        </StyledTopRow>
      </>
    ) : null}
    <Spacer y={1} />
  </StyledHeader>
);

const PageContent = ({ children }: { children?: ReactNode }) => (
  <StyledScroll>
    <StyledContent>
      <Spacer x={1.5} />
      <StyledContentColumn>
        {children}
        <Spacer y={2} />
      </StyledContentColumn>
      <Spacer x={1.5} />
    </StyledContent>
  </StyledScroll>
);

const PageFooter = ({ children }: { children?: ReactNode }) => (
  <StyledFooter>
    <Spacer y={1.5} />
    <StyledFooterRow>
      <Spacer x={0.5} />
      {children}
      <Spacer x={0.5} />
    </StyledFooterRow>
    <Spacer y={1} />
  </StyledFooter>
);

export const Page = ({
  tone = "light",
  onBack,
  title,
  subtitle,
  topActions,
  footerMenu,
  children,
}: PageProps) => (
  <StyledPage $tone={tone}>
    <ToastContainer />
    <PageHeader
      tone={tone}
      onBack={onBack}
      title={title}
      subtitle={subtitle}
      topActions={topActions}
    />
    <PageContent>{children}</PageContent>
    {footerMenu ? <PageFooter>{footerMenu}</PageFooter> : null}
  </StyledPage>
);

const StyledPage = styled(StyledColumn)<{ $tone: PageTone }>`
  width: 100%;
  height: 100dvh;
  overflow: hidden;
  background: ${({ $tone }) => TONE_STYLES[$tone].background};
  color: ${({ $tone }) => TONE_STYLES[$tone].text};
`;

// Fixed top band: brand, actions and title stay put while the content scrolls.
// Opaque background + stacking so scrolling content never shows through it.
const StyledHeader = styled(StyledColumn)<{ $tone: PageTone }>`
  flex-shrink: 0;
  width: 100%;
  position: relative;
  z-index: 1;
  background: ${({ $tone }) => TONE_STYLES[$tone].background};
`;

// Scrolls the page content while the header and footer stay in place.
const StyledScroll = styled(StyledColumn)`
  flex: 1;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;

const StyledTopBar = styled(StyledColumn)`
  width: 100%;
`;

const StyledTopRow = styled(StyledFlex)`
  width: 100%;
  align-items: center;
`;

// Pushes the actions to the right edge, on their own row below the brand.
const StyledTopActions = styled(StyledFlex)`
  margin-left: auto;
  align-items: center;
`;

const StyledTitleColumn = styled(StyledColumn)`
  align-items: flex-start;
`;

const StyledTitle = styled.div`
  ${typographies["Header/H1"]}
`;

const StyledSubtitle = styled.div`
  ${typographies["Body/XL"]}
  color: ${colors["colors/text/darkGrey"]};
`;

// Grows so that the footer stays at the bottom on short content.
const StyledContent = styled(StyledFlex)`
  width: 100%;
  flex: 1;
`;

// Takes the width between the page's horizontal gutters.
// min-width: 0 keeps long/unbreakable content from widening the page.
const StyledContentColumn = styled(StyledColumn)`
  flex: 1;
  min-width: 0;
`;

const StyledFooter = styled(StyledColumn)`
  flex-shrink: 0;
  background: ${colors["colors/surfaces/background/background_level0"]};
  box-shadow: ${MODAL_SHADOW};
`;

const StyledFooterRow = styled(StyledFlex)`
  width: 100%;
`;
